import React, { useEffect, useMemo, useRef, useState, useContext } from "react";
import axios from "axios";
import {
  Users,
  Plus,
  RotateCcw,
  Trash2,
  Pencil,
  X,
  Search,
  Check,
  RefreshCcw,
} from "lucide-react";
import { toast } from "sonner";
import { AuthContext } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

const API_BASE = "http://localhost:5000";

// ──────────────────────────────────────────────────────────────────────────────
// Axios instance + dev logger
// ──────────────────────────────────────────────────────────────────────────────
const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
});

const logRequestDetails = (config) => {
  if (process.env.NODE_ENV === "development") {
    console.log("Request URL:", config.url);
    console.log("Method:", (config.method || "").toUpperCase());
    console.log("Headers:", config.headers);
    if (config.data) console.log("Body:", config.data);
  }
  return config;
};

// ──────────────────────────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────────────────────────
const UserManagement = () => {
  const { token, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  // Tabs
  const [activeTab, setActiveTab] = useState("users"); // "users" | "approvals"

  // Users state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const usersPerPage = 10;

  // Supervisor filter + list
  const [supervisorFilter, setSupervisorFilter] = useState("");
  const [supervisorList, setSupervisorList] = useState([]);

  // Approvals state
  const [approvals, setApprovals] = useState([]);
  const [approvalsLoading, setApprovalsLoading] = useState(true);
  const [approvalsError, setApprovalsError] = useState(null);
  const [approvalsPage, setApprovalsPage] = useState(1);
  const approvalsPerPage = 10;
  const [approvalFilter, setApprovalFilter] = useState("all");
  

  // after other state hooks
  const [approvalRole, setApprovalRole] = useState({});
  const [approvalSupervisor, setApprovalSupervisor] = useState({});
  const [approvalDepartment, setApprovalDepartment] = useState({});
  const [approvalNbsID, setApprovalNbsID] = useState({});


  // Modals + forms
  const [modals, setModals] = useState({
    add: false,
    edit: false,
    delete: false,
    reset: false,
  });
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    name: "",
    department: "",
    role: "",
    nbsID: "",
    supervisor: "",
  });
  const modalRef = useRef(null);

  // Memoized auth header
  const authHeader = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );


  // Options derived from existing users (usercredentials)
  const [roleOptions, setRoleOptions] = useState([]);
  const [deptOptions, setDeptOptions] = useState([]);


  // ─── Interceptors (mount/unmount) ───────────────────────────────────────────
  useEffect(() => {
    const reqId = api.interceptors.request.use(
      (config) => {
        if (token) config.headers.Authorization = `Bearer ${token}`;
        logRequestDetails(config);
        return config;
      },
      (error) => Promise.reject(error)
    );

    const resId = api.interceptors.response.use(
      (res) => res,
      (error) => {
        if (error?.response) {
          const { status, data } = error.response;
          // Auth issues
          if (status === 401 || status === 403) {
            toast.error("Authentication failed. Please log in again.");
            logout?.();
            navigate("/login");
          } else if (status === 400) {
            toast.error(data?.message || "Bad request. Please check your input.");
          }
        } else if (error?.request) {
          toast.error("No response from server. Please check your connection.");
        } else {
          toast.error(error?.message || "An unexpected error occurred");
        }
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.request.eject(reqId);
      api.interceptors.response.eject(resId);
    };
  }, [token, logout, navigate]);

  // ─── Helpers ────────────────────────────────────────────────────────────────
  const handleError = (err, fallbackMessage) => {
    let msg = fallbackMessage;
    if (err?.response) {
      const { status, data } = err.response;
      if (status === 400) {
        if (data?.errors && typeof data.errors === "object") {
          msg =
            "Validation error: " +
            Object.entries(data.errors)
              .map(([field, e]) => `${field}: ${e}`)
              .join("; ");
        } else msg = data?.message || "Invalid request.";
      } else if (status === 404) msg = "Resource not found.";
      else if (status === 500) msg = "Server error. Try again later.";
      else msg = data?.message || msg;
    } else if (err?.request) {
      msg = "No server response. Check your connection.";
    } else if (err?.message) {
      msg = err.message;
    }
    toast.error(msg);
    return msg;
  };

  const resetForm = () =>
    setFormData({
      username: "",
      password: "",
      name: "",
      department: "",
      role: "",
      nbsID: "",
      supervisor: "",
    });

  const closeAllModals = () =>
    setModals({ add: false, edit: false, delete: false, reset: false });

  const openModal = (type, user = null) => {
    resetForm();
    setCurrentUser(user);
    if (user && type === "edit") setFormData({ ...user, password: "" });
    setModals({ add: false, edit: false, delete: false, reset: false, [type]: true });
  };

  // ─── Auth check + initial fetch ─────────────────────────────────────────────
  useEffect(() => {
    const run = async () => {
      if (!token) {
        toast.error("Not authenticated. Please log in.");
        navigate("/login");
        return;
      }
      try {
        // Optional token verification
        try {
          await api.get("/api/auth/verify-token", { headers: authHeader });
        } catch {
          // ignore if endpoint not available
        }
        await fetchUsers();
        await fetchApprovals();
      } catch (err) {
        // interceptor handles most auth issues
        console.error("Init error:", err);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ─── Fetchers ───────────────────────────────────────────────────────────────
  const fetchUsers = async () => {
    if (!token) return;
    setUsersLoading(true);
    setUsersError(null);
    try {
      const res = await api.get(`/api/users`, { headers: authHeader });
      const list = res.data || [];
      setUsers(list);

      // supervisors (already present)
      setSupervisorList([...new Set(list.map((u) => u.supervisor).filter(Boolean))]);

      // NEW: dynamic options
      const uniq = (arr) =>
        Array.from(new Set(arr.map((s) => (s || "").trim()).filter(Boolean))).sort((a, b) =>
          a.localeCompare(b)
        );

      setRoleOptions(uniq(list.map((u) => u.role)));
      setDeptOptions(uniq(list.map((u) => u.department)));
    } catch (err) {
      const msg = handleError(err, "Failed to load users");
      setUsersError(msg);
    } finally {
      setUsersLoading(false);
    }
  };


  const fetchApprovals = async () => {
    if (!token) return;
    setApprovalsLoading(true);
    setApprovalsError(null);
    try {
      const qs =
        approvalFilter && approvalFilter !== "all" ? `?status=${approvalFilter}` : "?status=all";
      const res = await api.get(`/api/access-requests${qs}`, { headers: authHeader });
      const list = Array.isArray(res.data) ? res.data : [];
      setApprovals(list);

      // NEW: merge department options from approvals
      const uniq = (arr) =>
        Array.from(new Set(arr.map((s) => (s || "").trim()).filter(Boolean))).sort((a, b) =>
          a.localeCompare(b)
        );

      const deptFromApprovals = uniq(
        list.map((r) => r.requestedDepartment || r.department || "")
      );

      setDeptOptions((prev) => {
        const merged = [...(prev || []), ...deptFromApprovals];
        return uniq(merged);
      });
    } catch (err) {
      const msg = handleError(err, "Failed to load access requests");
      setApprovalsError(msg);
    } finally {
      setApprovalsLoading(false);
    }
  };



  // Refetch approvals when filter changes
  useEffect(() => {
    setApprovalsPage(1);
    fetchApprovals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvalFilter]);

  // ─── CRUD: Users ────────────────────────────────────────────────────────────
  const handleSubmit = async (e, isEdit = false) => {
    e.preventDefault();
    if (!formData.username || !formData.name || !formData.department || !formData.role) {
      return toast.error("Please complete all fields");
    }
    if (!isEdit && (!formData.password || formData.password.length < 8)) {
      return toast.error("Password must be at least 8 characters");
    }

    try {
      const userData = { ...formData };
      if (isEdit && !userData.password) delete userData.password;

      // Try wrapped payload first, then fallback to unwrapped
      const url = isEdit
        ? `/api/users/${encodeURIComponent(currentUser.username)}`
        : `/api/users`;
      const method = isEdit ? api.put : api.post;

      try {
        await method(url, { user: userData }, { headers: authHeader });
      } catch {
        await method(url, userData, { headers: authHeader });
      }

      toast.success(`User ${isEdit ? "updated" : "added"} successfully`);
      await fetchUsers();
      closeAllModals();
      resetForm();
    } catch (err) {
      if (err?.response?.status === 409) toast.error("Username already exists.");
      else handleError(err, `Failed to ${isEdit ? "update" : "add"} user`);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/api/users/${encodeURIComponent(currentUser.username)}`, {
        headers: authHeader,
      });
      toast.success("User deleted successfully");
      await fetchUsers();
      closeAllModals();
    } catch (err) {
      handleError(err, "Failed to delete user");
    }
  };

  const handleResetPassword = async (user) => {
    try {
      await api.post(
        `/api/auth/reset-password`,
        { username: user.username, newPassword: "default123" },
        { headers: authHeader }
      );
      toast.success(`Password for ${user.username} reset to "default123"`);
      closeAllModals();
    } catch (err) {
      handleError(err, "Failed to reset password");
    }
  };

  const [approvingIds, setApprovingIds] = useState(new Set());

  // ─── Approvals: actions ─────────────────────────────────────────────────────
const approveRequest = async (reqIdOrObj) => {
  const rawId = typeof reqIdOrObj === "string" ? reqIdOrObj : getReqId(reqIdOrObj);
  const urlSafeId = encodeURIComponent(rawId);

  const role = approvalRole[rawId];
  const supervisor = approvalSupervisor[rawId];
  const department = approvalDepartment?.[rawId];

  if (!role) return toast.error("Please select a role before approving.");
  if (!supervisor) return toast.error("Please select a supervisor before approving.");
  if (!department) return toast.error("Please select a department before approving.");

  // Pull NBS ID: prefer per-row state if you later add an input, else from the request object
  const reqRow = approvals.find((r) => getReqId(r) === rawId) || {};
  const nbsIDRaw =
    (approvalNbsID?.[rawId] ?? reqRow.nbsID ?? reqRow.nbsid ?? reqRow.nbsId ?? "") + "";
  const nbsID = nbsIDRaw.trim().replace(/\s+/g, ""); // simple sanitize

  if (!nbsID) return toast.error("Please provide an NBS ID before approving.");

  setApprovingIds((prev) => new Set(prev).add(rawId));

  try {
    // Send both nbsID and nbsid to be safe with backend casing
    await api.post(
      `/api/access-requests/${urlSafeId}/approve`,
      { role, supervisor, department, nbsID, nbsid: nbsID },
      { headers: authHeader }
    );

    // Best-effort email (fire-and-forget)
    const recipientCandidate =
      reqRow.username ||
      reqRow.requestorEmail ||
      reqRow.email ||
      reqRow.requestorUsername ||
      reqRow.userEmail ||
      "";

    if (recipientCandidate && recipientCandidate.includes("@")) {
      void api
        .post(
          "/api/emails/emailapprovalaccess",
          { recipients: [recipientCandidate], subject: "Your account has been approved" },
          { headers: authHeader }
        )
        .then(() => toast.success("Approval email sent"))
        .catch((emailErr) => {
          console.error("Email notification failed:", emailErr);
          toast.error("Approved, but failed to send email");
        });
    } else {
      toast.message("Approved (no email found to notify)");
    }

    if (approvalFilter === "pending") {
      setApprovals((prev) => prev.filter((r) => getReqId(r) !== rawId));
    } else {
      await fetchApprovals();
    }
    await fetchUsers();

    toast.success("Signup request approved");
  } catch (err) {
    handleError(err, "Approve failed");
  } finally {
    setApprovingIds((prev) => {
      const next = new Set(prev);
      next.delete(rawId);
      return next;
    });
  }
};



  const denyRequest = async (reqIdOrObj) => {
    const id = typeof reqIdOrObj === "string" ? reqIdOrObj : getReqId(reqIdOrObj);
    try {
      setApprovals((prev) =>
        prev.map((r) => (getReqId(r) === id ? { ...r, __optimistic: "denied" } : r))
      );
      await api.post(`/api/access-requests/${id}/deny`, {}, { headers: authHeader });

      if (approvalFilter === "pending") {
        setApprovals((prev) => prev.filter((r) => getReqId(r) !== id));
      } else {
        fetchApprovals();
      }
      toast.success("Signup request denied");
    } catch (err) {
      handleError(err, "Deny failed");
      fetchApprovals();
    }
  };



  // ─── Filters + pagination (Users) ───────────────────────────────────────────
  const filteredUsers = users.filter((u) => {
    const matchesText = [u.username, u.name, u.department, u.role]
      .filter(Boolean)
      .some((f) => f.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesSupervisor = !supervisorFilter || u.supervisor === supervisorFilter;
    return matchesText && matchesSupervisor;
  });

  useEffect(() => setCurrentPage(1), [searchTerm, supervisorFilter]);

  const totalUsers = filteredUsers.length;
  const usersTotalPages = Math.max(1, Math.ceil(totalUsers / usersPerPage));
  const visibleUsers = filteredUsers.slice(
    (currentPage - 1) * usersPerPage,
    currentPage * usersPerPage
  );
  const startIndex = totalUsers === 0 ? 0 : (currentPage - 1) * usersPerPage + 1;
  const endIndex = Math.min(currentPage * usersPerPage, totalUsers);

  // ─── Pagination (Approvals) ─────────────────────────────────────────────────
  const totalApprovals = approvals.length;
  const approvalsTotalPages = Math.max(1, Math.ceil(totalApprovals / approvalsPerPage));
  const visibleApprovals = approvals.slice(
    (approvalsPage - 1) * approvalsPerPage,
    approvalsPage * approvalsPerPage
  );

  // ─── UI helpers ─────────────────────────────────────────────────────────────
  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: "bg-purple-100 text-purple-700",
      manager: "bg-yellow-100 text-yellow-700",
      supervisor: "bg-green-100 text-green-700",
      agent: "bg-yellow-100 text-yellow-700",
      pasig: "bg-blue-100 text-blue-700",
    };
    return colors[(role || "").toLowerCase()] || "bg-gray-100 text-gray-700";
  };

  // ID from Data API: {_id:{ $oid }} OR plain string OR 'ObjectId("...")'
  const getReqId = (r) =>
    String(r?._id?.$oid ?? r?._id ?? "")
      .replace(/^ObjectId\("(.+)"\)$/, "$1") // unwrap ObjectId("...")
      .trim();

  // Convenience label for Access/Role
  const accessRoleLabel = (r) => {
    const a = r.requestedAccess || "";
    const role = r.requestedRole || "";
    if (a && role) return `${a} / ${role}`;
    return a || role || "-";
  };


  // ─── Modals ─────────────────────────────────────────────────────────────────
  const AddEditModal = ({ isEdit }) => (
    <div
      className={`fixed inset-0 bg-black/50 flex items-center justify-center z-50 ${modals[isEdit ? "edit" : "add"] ? "" : "hidden"
        }`}
      onClick={(e) => e.target === e.currentTarget && closeAllModals()}
    >
      <div
        ref={modalRef}
        className="bg-white dark:bg-laryzeDarkGray rounded-lg shadow-xl p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {isEdit ? "Edit" : "Add"} User
          </h3>
          <button onClick={closeAllModals} className="text-gray-400 hover:text-gray-500">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSubmit(e, isEdit);
          }}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Username
              </label>
              <input
                type="text"
                value={formData.username}
                disabled={isEdit}
                onChange={(e) => setFormData((s) => ({ ...s, username: e.target.value }))}
                className="mt-1 block w-full px-3 py-2 border rounded-md focus:ring-uasYellow focus:border-uasYellow"
              />
            </div>

            {!isEdit && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Password
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData((s) => ({ ...s, password: e.target.value }))}
                  className="mt-1 block w-full px-3 py-2 border rounded-md focus:ring-uasYellow focus:border-uasYellow"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Full Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData((s) => ({ ...s, name: e.target.value }))}
                className="mt-1 block w-full px-3 py-2 border rounded-md focus:ring-uasYellow focus:border-uasYellow"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Department
              </label>
              <select
                value={formData.department}
                onChange={(e) => setFormData((s) => ({ ...s, department: e.target.value }))}
                className="mt-1 block w-full px-3 py-2 border rounded-md focus:ring-uasYellow focus:border-uasYellow"
              >
                <option value="">Select Department</option>
                {deptOptions.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>


            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Supervisor
              </label>
              <select
                value={formData.supervisor || ""}
                onChange={(e) => setFormData((s) => ({ ...s, supervisor: e.target.value }))}
                className="mt-1 block w-full px-3 py-2 border rounded-md focus:ring-uasYellow focus:border-uasYellow"
              >
                <option value="">Select Supervisor</option>
                {supervisorList.map((s, i) => (
                  <option key={i} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Role
              </label>
              <select
                value={formData.role}
                onChange={(e) => setFormData((s) => ({ ...s, role: e.target.value }))}
                className="mt-1 block w-full px-3 py-2 border rounded-md focus:ring-uasYellow focus:border-uasYellow"
              >
                <option value="">Select Role</option>
                {roleOptions.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>


            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                NBS ID
              </label>
              <input
                type="text"
                value={formData.nbsID}
                onChange={(e) => setFormData((s) => ({ ...s, nbsID: e.target.value }))}
                className="mt-1 block w-full px-3 py-2 border rounded-md focus:ring-uasYellow focus:border-uasYellow"
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              onClick={closeAllModals}
              className="px-4 py-2 border rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-md text-sm font-medium text-white dark:bg-uasYellow dark:text-black bg-black hover:bg-uasYellow hover:text-black transition"
            >
              {isEdit ? "Update" : "Add"} User
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const DeleteModal = () => (
    <div
      className={`fixed inset-0 bg-black/50 flex items-center justify-center z-50 ${modals.delete ? "" : "hidden"
        }`}
      onClick={(e) => e.target === e.currentTarget && closeAllModals()}
    >
      <div
        className="bg-white dark:bg-laryzeDarkGray rounded-lg shadow-xl p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete User</h3>
          <button onClick={closeAllModals} className="text-gray-400 hover:text-gray-500">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-gray-600 dark:text-gray-300 mb-4">
          Are you sure you want to delete <strong>{currentUser?.name}</strong>? This action cannot
          be undone.
        </p>
        <div className="flex justify-end gap-2">
          <button onClick={closeAllModals} className="px-4 py-2 border rounded-md">
            Cancel
          </button>
          <button
            onClick={handleDelete}
            className="px-4 py-2 rounded-md text-white bg-red-600 hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );

  const ResetModal = () => (
    <div
      className={`fixed inset-0 bg-black/50 flex items-center justify-center z-50 ${modals.reset ? "" : "hidden"
        }`}
      onClick={(e) => e.target === e.currentTarget && closeAllModals()}
    >
      <div
        className="bg-white dark:bg-laryzeDarkGray rounded-lg shadow-xl p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Reset Password</h3>
          <button onClick={closeAllModals} className="text-gray-400 hover:text-gray-500">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-gray-700 dark:text-gray-300 mb-6">
          Reset password for <strong>{currentUser?.username}</strong> to{" "}
          <strong>"default123"</strong>?
        </p>
        <div className="flex justify-end gap-2">
          <button onClick={closeAllModals} className="px-4 py-2 border rounded-md">
            Cancel
          </button>
          <button
            onClick={() => handleResetPassword(currentUser)}
            className="px-4 py-2 rounded-md text-white bg-uasYellow dark:bg-black dark:hover:bg-gray-800 hover:bg-yellow-600"
          >
            Confirm Reset
          </button>
        </div>
      </div>
    </div>
  );

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="lg:p-6 font-montserrat">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between bg-black dark:bg-uasYellow px-6 py-2 rounded-t-lg">
        <h1 className="text-lg font-bold flex items-center text-white dark:text-black mb-4 md:mb-0">
          <Users className="w-5 h-5 mr-2" /> User Management
        </h1>

        {/* Right actions */}
        <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
          {/* Search */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-2 pointer-events-none">
              <Search className="w-3.5 h-3.5 text-gray-500" />
            </div>
            <input
              type="text"
              className="pl-7 pr-3 py-1.5 text-sm border rounded-md focus:ring-1 focus:ring-uasYellow focus:border-uasYellow w-full md:w-56"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Supervisor Filter */}
          <select
            value={supervisorFilter}
            onChange={(e) => setSupervisorFilter(e.target.value)}
            className="px-2 py-1.5 text-sm border rounded-md focus:ring-1 focus:ring-uasYellow focus:border-uasYellow w-full md:w-48"
          >
            <option value="">All Supervisors</option>
            {supervisorList.map((sup, i) => (
              <option key={i} value={sup}>
                {sup}
              </option>
            ))}
          </select>

          {/* Add */}
          <button
            onClick={() => openModal("add")}
            className="flex items-center justify-center bg-uasYellow text-black dark:bg-black dark:text-white rounded-md px-3 py-1.5 text-sm font-medium shadow hover:shadow-md transition"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Add
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-laryzeDarkGray px-6 pt-4">
        <div className="flex gap-3 border-b border-gray-200 dark:border-gray-700">
          <button
            className={`px-3 py-2 -mb-px border-b-2 ${activeTab === "users"
              ? "border-yellow-400 text-black dark:text-white font-semibold"
              : "border-transparent text-gray-500"
              }`}
            onClick={() => setActiveTab("users")}
          >
            Users <span className="text-xs text-gray-400">({users.length})</span>
          </button>

          <button
            className={`px-3 py-2 -mb-px border-b-2 ${activeTab === "approvals"
              ? "border-yellow-400 text-black dark:text-white font-semibold"
              : "border-transparent text-gray-500"
              }`}
            onClick={() => setActiveTab("approvals")}
          >
            Access Approval{" "}
            <span className="text-xs text-gray-400">({approvals.length})</span>
          </button>
          <button
            onClick={() => (activeTab === "users" ? fetchUsers() : fetchApprovals())}
            className="
    ml-auto inline-flex items-center gap-1 text-sm px-3 py-1 rounded
    border
    bg-white text-gray-700 border-gray-300 hover:bg-gray-50
    dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:bg-gray-700
  "
          >
            <RefreshCcw className="w-4 h-4" />
            Refresh
          </button>

        </div>
      </div>

      {/* Tab Panels */}
      <div className="bg-white dark:bg-laryzeDarkGray rounded-b-lg shadow overflow-hidden">
        {/* USERS TAB */}
        {activeTab === "users" && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 dark:bg-gray-800 text-xs uppercase text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="px-6 py-3">Username</th>
                  <th className="px-6 py-3">Name</th>
                  <th className="px-6 py-3">Department</th>
                  <th className="px-6 py-3">Role</th>
                  <th className="px-6 py-3">NBSID</th>
                  <th className="px-6 py-3">Supervisor</th>
                  <th className="px-6 py-3">Action</th>   {/* NEW */}

                </tr>
              </thead>




              <tbody>
                {usersLoading ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-4 text-center">
                      Loading...
                    </td>
                  </tr>
                ) : usersError ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-4 text-center text-red-600">
                      {usersError}
                    </td>
                  </tr>
                ) : visibleUsers.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-4 text-center">
                      No users found
                    </td>
                  </tr>
                ) : (
                  visibleUsers.map((u) => (
                    <tr
                      key={u._id}
                      className="border-b dark:text-white dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <td className="px-4 py-2 font-medium">{u.username}</td>
                      <td className="px-4 py-2">{u.name}</td>
                      <td className="px-4 py-2">{u.department}</td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-1 text-xs rounded-full ${getRoleBadgeColor(u.role)}`}>
                          {u.role}
                        </span>
                      </td>
                      <td className="px-4 py-2">{u.nbsID}</td>
                      <td className="px-4 py-2">{u.supervisor}</td>
                      <td className="px-4 py-2 space-x-2">
                        <button
                          onClick={() => openModal("reset", u)}
                          className="bg-gray-700 text-white px-2 py-1 text-xs rounded hover:bg-gray-800"
                        >
                          <RotateCcw className="inline-block w-4 h-4 mr-1" /> Reset
                        </button>
                        <button
                          onClick={() => openModal("delete", u)}
                          className="bg-red-500 text-white px-2 py-1 text-xs rounded hover:bg-red-600"
                        >
                          <Trash2 className="inline-block w-4 h-4 mr-1" /> Delete
                        </button>
                        <button
                          onClick={() => openModal("edit", u)}
                          className="bg-uasYellow text-black px-2 py-1 text-xs rounded hover:bg-yellow-500 dark:bg-black dark:text-white dark:hover:bg-gray-800"
                        >
                          <Pencil className="inline-block w-4 h-4 mr-1" /> Edit
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Users pagination */}
            {!usersLoading && !usersError && (
              <>
                {totalUsers > 0 ? (
                  <div className="flex flex-col md:flex-row md:items-center justify-between px-6 py-4 text-sm">
                    <span className="text-gray-600 dark:text-gray-300 mb-3 md:mb-0">
                      Showing {startIndex}-{endIndex} of {totalUsers} users
                    </span>
                    <div className="flex items-center">
                      <span className="text-gray-700 dark:text-gray-300 mr-2">
                        Page {currentPage} of {usersTotalPages}
                      </span>
                      <div className="flex space-x-1">
                        <button
                          onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                          disabled={currentPage === 1}
                          className="w-8 h-8 flex items-center justify-center border rounded disabled:opacity-50 disabled:cursor-not-allowed dark:bg-uasYellow dark:border-none"
                          aria-label="Previous page"
                        >
                          &lt;
                        </button>
                        <button
                          onClick={() => setCurrentPage((p) => Math.min(p + 1, usersTotalPages))}
                          disabled={currentPage === usersTotalPages}
                          className="w-8 h-8 flex items-center justify-center border rounded disabled:opacity-50 disabled:cursor-not-allowed dark:bg-uasYellow dark:border-none"
                          aria-label="Next page"
                        >
                          &gt;
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                    No users match the selected filters. Try changing your search criteria.
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* APPROVALS TAB */}
        {activeTab === "approvals" && (
          <div className="overflow-x-auto">
            {/* status filter + refresh row */}
            <div className="px-6 pt-4 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
              <div className="flex items-center gap-2">
                <label htmlFor="approval-filter" className="text-sm text-gray-600 dark:text-gray-300">
                  Status:
                </label>
                <select
                  id="approval-filter"
                  className="border rounded px-2 py-1 text-sm"
                  value={approvalFilter}
                  onChange={(e) => setApprovalFilter(e.target.value)}
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="denied">Denied</option>
                  <option value="all">All</option>
                </select>
              </div>

            </div>

            <table className="w-full text-sm text-left mt-2">
          <thead className="bg-gray-50 dark:bg-gray-800 text-xs uppercase text-gray-500 dark:text-gray-400">
  <tr>
    <th className="px-6 py-3">NBSID</th>
    <th className="px-6 py-3">Name</th>
    <th className="px-6 py-3">Username (Email)</th>
    <th className="px-6 py-3">Reason</th>
    <th className="px-6 py-3">Status</th>
    <th className="px-6 py-3">Requested At</th>
    <th className="px-6 py-3">Supervisor</th>
    <th className="px-6 py-3">Department</th> {/* NEW */}
    <th className="px-6 py-3">Role</th>
    <th className="px-6 py-3">Actions</th>
  </tr>
</thead>




              <tbody>
                {approvalsLoading ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center">
                      Loading access requests...
                    </td>
                  </tr>
                ) : approvalsError ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center text-red-600">
                      {approvalsError}
                    </td>
                  </tr>
                ) : visibleApprovals.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center">
                      No requests found.
                    </td>
                  </tr>
                ) : (
                  visibleApprovals.map((req) => {
                    const id = getReqId(req);
                    const status = (req.status || req.__optimistic || "pending").toLowerCase();
                    const badge =
                      status === "approved"
                        ? "bg-green-100 text-green-700"
                        : status === "denied"
                          ? "bg-red-100 text-red-700"
                          : "bg-yellow-100 text-yellow-800";

                    return (
                      <tr key={id} className="border-b dark:text-white dark:border-gray-700">
                        {/* NBSID */}
                        <td className="px-4 py-2">{req.nbsid || "-"}</td>

                        {/* Name */}
                        <td className="px-4 py-2">{req.name || "-"}</td>

                        {/* Username (Email) */}
                        <td className="px-4 py-2">
                          {req.username || req.requestorEmail || req.requestorUsername || "-"}
                        </td>

                        {/* Reason */}
                        <td className="px-4 py-2">
                          <span title={req.reason || ""}>
                            {(req.reason || "-").length > 60
                              ? (req.reason || "-").slice(0, 60) + "…"
                              : req.reason || "-"}
                          </span>
                        </td>

                        {/* Status */}
                        <td className="px-4 py-2">
                          <span className={`px-2 py-1 text-xs rounded-full ${badge}`}>{status}</span>
                        </td>

                        {/* Requested At */}
                        <td className="px-4 py-2">
                          {req.createdAt ? new Date(req.createdAt).toLocaleString() : "-"}
                        </td>

                        {/* Supervisor select */}
                        <td className="px-4 py-2">
                          <select
                            className="
                w-44 px-2 py-1 text-xs rounded-md
                border border-gray-300 bg-white text-gray-900
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                disabled:opacity-50 disabled:cursor-not-allowed
                dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600
                dark:focus:ring-blue-400 dark:focus:border-blue-400
              "
                            value={approvalSupervisor[id] ?? ""}
                            onChange={(e) =>
                              setApprovalSupervisor((prev) => ({ ...prev, [id]: e.target.value }))
                            }
                            disabled={status !== "pending"}
                          >
                            <option value="">Select supervisor</option>
                            {supervisorList.map((sup) => (
                              <option key={sup} value={sup}>
                                {sup}
                              </option>
                            ))}
                          </select>
                        </td>

                        {/* Department select (NEW) */}
<td className="px-4 py-2">
  <select
    className="
      w-44 px-2 py-1 text-xs rounded-md
      border border-gray-300 bg-white text-gray-900
      focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
      disabled:opacity-50 disabled:cursor-not-allowed
      dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600
      dark:focus:ring-blue-400 dark:focus:border-blue-400
    "
    value={approvalDepartment[id] ?? req.requestedDepartment ?? req.department ?? ""} // NEW
    onChange={(e) =>
      setApprovalDepartment((prev) => ({ ...prev, [id]: e.target.value }))
    }
    disabled={status !== "pending"}
  >
    <option value="">Select department</option>
    {deptOptions.map((dept) => (
      <option key={dept} value={dept}>
        {dept}
      </option>
    ))}
  </select>
</td>


                        {/* Role select */}
                        <td className="px-4 py-2">
                          <select
                            className="
                border rounded px-2 py-1 text-xs
                bg-white text-gray-900 border-gray-300
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600
                dark:focus:ring-blue-400 dark:focus:border-blue-400
              "
                            value={approvalRole[id] ?? req.requestedRole ?? ""}
                            onChange={(e) =>
                              setApprovalRole((prev) => ({ ...prev, [id]: e.target.value }))
                            }
                            disabled={status !== "pending"}
                          >
                            <option value="">Select role</option>
                            {roleOptions.map((role) => (
                              <option key={role} value={role}>
                                {role}
                              </option>
                            ))}
                          </select>
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-2 space-x-2">
                          <button
                            className="
    inline-flex items-center gap-1
    bg-green-600 text-white
    px-2 py-1 text-xs rounded
    hover:bg-green-700
    disabled:opacity-50 disabled:cursor-not-allowed
  "
                            onClick={() => approveRequest(id)}
                     disabled={
  status !== "pending" ||
  !approvalRole[id] ||
  !approvalSupervisor[id] ||
  !approvalDepartment[id] ||    // NEW
  approvingIds.has(id)
}
title={
  approvingIds.has(id)
    ? "Approving..."
    : status !== "pending"
      ? "Only pending items can be approved"
      : !approvalRole[id]
        ? "Select a role first"
        : !approvalSupervisor[id]
          ? "Select a supervisor first"
          : !approvalDepartment[id]               // NEW
            ? "Select a department first"
            : "Approve"
}

                          >
                            {approvingIds.has(id) ? (
                              // simple inline spinner (no extra lib)
                              <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25" />
                                <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
                              </svg>
                            ) : (
                              <Check className="w-4 h-4" />
                            )}
                            <span>{approvingIds.has(id) ? "Approving..." : "Approve"}</span>
                          </button>

                          <button
                            className="
                inline-flex items-center gap-1
                bg-red-500 text-white
                px-2 py-1 text-xs rounded
                hover:bg-red-600
                disabled:opacity-50 disabled:cursor-not-allowed
              "
                            onClick={() => denyRequest(id)}
                            disabled={status !== "pending"}
                            title="Deny"
                          >
                            <X className="w-4 h-4" />
                            <span>Deny</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>

            </table>

            {/* Approvals pagination */}
            {!approvalsLoading && !approvalsError && totalApprovals > 0 && (
              <div className="flex items-center justify-between px-6 py-4 text-sm">
                <span className="text-gray-600 dark:text-gray-300">
                  Page {approvalsPage} of {approvalsTotalPages}
                </span>
                <div className="flex space-x-1">
                  <button
                    onClick={() => setApprovalsPage((p) => Math.max(p - 1, 1))}
                    disabled={approvalsPage === 1}
                    className="w-8 h-8 flex items-center justify-center border rounded disabled:opacity-50 disabled:cursor-not-allowed dark:bg-uasYellow dark:border-none"
                    aria-label="Previous page"
                  >
                    &lt;
                  </button>
                  <button
                    onClick={() => setApprovalsPage((p) => Math.min(p + 1, approvalsTotalPages))}
                    disabled={approvalsPage === approvalsTotalPages}
                    className="w-8 h-8 flex items-center justify-center border rounded disabled:opacity-50 disabled:cursor-not-allowed dark:bg-uasYellow dark:border-none"
                    aria-label="Next page"
                  >
                    &gt;
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      <AddEditModal isEdit={false} />
      <AddEditModal isEdit={true} />
      <DeleteModal />
      <ResetModal />
    </div>
  );
};

export default UserManagement;
